<script lang="ts">
    import {getCurrentLanguage, getLogger} from 'src/core';
    import type {FormControlOptions} from '../../forms/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {NumberValueEditorValidations} from '../number/types';
    import type {FormGroup} from '../../forms/form-group';
    import type {ValueEditorSize} from '../../../types';
    import {FormControlBuilder} from '../../forms/form-control';
    import {formControls} from '../../forms/use.form-controls';
    import {style} from 'svelte-forms';
    import {getContext, onDestroy} from 'svelte';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {validatorFactory} from '../number/utils';
    import {emptyAsNullParser, throwParseErrorIfNaN} from '../_shared/editors-utils';
    import {createInputDescription} from '../../description/utils';
    import {FormGroupBuilder} from '../../forms/form-group';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {required} from '../../forms/use.required';
    import {getValueEditorLabelContext} from '../../label/value-editor-label-context';
    import {getOptionById, identifierToModelParser, modelToIdentifierFormatter, tryToFindOptionById} from './utils';
    import {isDefined, isNullOrUndefined} from 'shared/utils/custom-utils';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import Selectbox from '../_shared/Selectbox.svelte';
    import type {
        PriceValueEditorLocalizations,
        PriceValueEditorModel,
        PriceValueEditorOptions,
        CurrencyOption
    } from './types';

    export let model: PriceValueEditorModel;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: PriceValueEditorOptions;
    export let validations: NumberValueEditorValidations;


    const logger = getLogger();
    const currentLanguage = getCurrentLanguage();
    const localize: EditorsLocalizationFunction<PriceValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);
    const labelContext = getValueEditorLabelContext();

    normalizeModel();
    let validationsWithDefaultMin = addDefaultMinValidation(validations);
    $: validationsWithDefaultMin = addDefaultMinValidation(validations);

    const formControlInput = FormControlBuilder.for(`${editorId}-amount`, `${editorName}-amount`, model?.amount)
        .withParsers([throwParseErrorIfNaN])
        .withValidators(validatorFactory(validationsWithDefaultMin))
        .withOptions(formControlOptions)
        .build(logger);

    const formControlCurrency = FormControlBuilder.for(`${editorId}-currency`, `${editorName}-currency`, model?.currency)
        .withParsers([identifierToModelParser(options.currencyOptions)])
        .withFormatters([modelToIdentifierFormatter()])
        .build(logger);

    const selectedCurrencyId$ = formControlCurrency.getViewValue$();

    const formGroupBuilder = new FormGroupBuilder<PriceValueEditorModel>().addModifiers([emptyAsNullParser(options)]);
    formGroupBuilder.registerFormControl(formControlInput);
    formGroupBuilder.registerFormControl(formControlCurrency);

    const formControl = formGroupBuilder.build(editorId, editorName, new Map(
        [[`${editorName}-currency`, 'currency'], [`${editorName}-amount`, 'amount']]
    ));

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    onDestroy(() => {
        subscription.unsubscribe();
    });

    function normalizeModel() {
        if (!model) {
            model = {currency: options.currencyOptions[0], amount: undefined};
        }
        if (!model.currency) {
            model.currency = options.currencyOptions[0];
        }
        if (typeof model.amount !== 'number') {
            model.amount = undefined;
        }
    }

    // price should have default min=0 validation (non negative value of amount)
    function addDefaultMinValidation(numberValueEditorValidations: NumberValueEditorValidations): NumberValueEditorValidations {
        if (isDefined(numberValueEditorValidations.min) && numberValueEditorValidations.min >= 0) {
            return numberValueEditorValidations;
        }

        return {...numberValueEditorValidations, min: 0};
    }

    function addModelCurrencyIfNotInOptions(currencyOptions: CurrencyOption[], modelValue: PriceValueEditorModel): CurrencyOption[] {
        if (isNullOrUndefined(modelValue)) {
            return currencyOptions;
        }

        const option = tryToFindOptionById(currencyOptions, modelValue.currency.id);
        if (isDefined(option)) {
            return currencyOptions;

        }

        return [...currencyOptions, modelValue.currency];
    }

    export function getFormController(): FormGroup<PriceValueEditorModel> {
        return formControl;
    }
</script>

<div class="input-group input-group-{size}" role="group" aria-labelledby={labelContext?.labelId ?? null}>
    <input type="number"
           id={editorId}
           name={editorName}
           class="form-control input-{size}"

           lang={currentLanguage}
           inputmode="decimal"

           {placeholder}
           disabled={isDisabled}

           step="0.01"
           min={validationsWithDefaultMin.min}
           max={validationsWithDefaultMin.max}
           use:required={validationsWithDefaultMin.required}

           use:formControls={formControlInput}
           use:style={{field: formControlInput.getFieldStateAsStore()}}

           use:focusOnMount={isFocused}
           use:touched={{formControl:formControlInput}}

           use:ariaInvalid={formControl}
           use:ariaErrormessage={formControl}
           use:ariaDescribedby={inputDescription}
           aria-labelledby="{editorId}-main-input-label"

           data-main-input/>

    <span id="{editorId}-main-input-label" class="sr-only">
        {labelContext?.labelText ?? ''} - {localize('amountInputLabel')}
    </span>

    {#if addModelCurrencyIfNotInOptions(options.currencyOptions, model).length === 1 }
        <div class="input-group-addon">
            <span class="currency">{options.currencyOptions[0].text}</span>
        </div>
    {:else}
        <div class="input-group-btn">

        <span id="{editorId}-metadata-select-label" class="sr-only">
            {labelContext?.labelText ?? ''} - {localize('currencySelectLabel')}.
            {localize('currentSelectValue')}:
            <!-- after this is text content of select (added by headless-ui) -->
        </span>
            <Selectbox selected={$selectedCurrencyId$}
                       disabled={isDisabled}
                       {size}
                       selectboxOptions={options.currencyOptions.map((currencyOption) => currencyOption.id)}
                       ariaLabelledby="{editorId}-metadata-select-label"
                       on:change={(event) => {formControlCurrency.setViewValue(event.detail); formControl.setTouched()}}>

                <svelte:fragment slot="button">
                    {getOptionById(addModelCurrencyIfNotInOptions(options.currencyOptions, model), $selectedCurrencyId$).text}
                </svelte:fragment>

                <svelte:fragment slot="option" let:option>
                    {getOptionById(options.currencyOptions, option).text}
                </svelte:fragment>
            </Selectbox>
        </div>
    {/if}
</div>
<!-- errors are taken directly from formControlInput because formControlCurrency cannot have error and formControlInput
    don't have prefix in errors like formControl would have -->
<ErrorMessages formController={formControl} errors={formControlInput.getErrors$()} {forceShowErrors} {size}/>
<Description {inputDescription}/>
